import { z } from "zod";
import { SystemEntity } from "./systemEntities";

/**
 * Metric cards a task type shows above its queue on a Task Page. Each type
 * picks its own cards in the task type editor; null means the defaults.
 */

export const TASK_TYPE_METRIC_KINDS = [
  "open_count",
  "unassigned",
  "aging",
  "sum",
] as const;

export type TaskTypeMetricKind = (typeof TASK_TYPE_METRIC_KINDS)[number];

/** Input-schema field types a `sum` card may total. */
export const TASK_TYPE_METRIC_SUM_FIELD_TYPES = [
  "integer",
  "float",
  "numeric",
  "currency",
] as const;

export function isTaskTypeMetricSumFieldType(fieldType: string): boolean {
  return (TASK_TYPE_METRIC_SUM_FIELD_TYPES as readonly string[]).includes(
    fieldType,
  );
}

/**
 * A `sum` card's field may instead name a money column of the task's linked
 * record, as `anchor.<field>`: an AR task stores no amount of its own, but
 * its sales order or invoice does.
 */
export const TASK_TYPE_METRIC_ANCHOR_PREFIX = "anchor.";

/** Linked-record fields a `sum` card may total, per anchor type. */
export const TASK_TYPE_METRIC_ANCHOR_SUM_FIELDS: Partial<
  Record<SystemEntity, readonly string[]>
> = {
  [SystemEntity.AR_SALES_ORDER]: ["totalAmount"],
  [SystemEntity.AR_INVOICE]: ["balanceDue", "grossAmount"],
};

/** The linked-record field a `sum` card names, or null for an input field. */
export function taskTypeMetricAnchorField(field: string): string | null {
  return field.startsWith(TASK_TYPE_METRIC_ANCHOR_PREFIX)
    ? field.slice(TASK_TYPE_METRIC_ANCHOR_PREFIX.length)
    : null;
}

/** The `anchor.<field>` values a sum card may name for this anchor type. */
export function taskTypeMetricAnchorSumFields(
  anchorEntityType: string | null | undefined,
): string[] {
  const fields = Object.entries(TASK_TYPE_METRIC_ANCHOR_SUM_FIELDS).find(
    ([type]) => type === anchorEntityType,
  )?.[1];
  return (fields ?? []).map(
    (field) => `${TASK_TYPE_METRIC_ANCHOR_PREFIX}${field}`,
  );
}

/** Age buckets (days since the task was created) on the aging card. */
export const TASK_METRIC_AGING_BUCKETS = ["0-2", "3-7", "8-14", "15+"] as const;

export type TaskMetricAgingBucket = (typeof TASK_METRIC_AGING_BUCKETS)[number];

export const MAX_TASK_TYPE_METRICS = 6;
export const MAX_TASK_TYPE_METRIC_LABEL_LENGTH = 60;

const label = z
  .string()
  .trim()
  .max(MAX_TASK_TYPE_METRIC_LABEL_LENGTH)
  .optional();

export const TaskTypeMetricSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("open_count"), label }),
  z.object({ kind: z.literal("unassigned"), label }),
  z.object({ kind: z.literal("aging"), label }),
  z.object({
    kind: z.literal("sum"),
    // A numeric input-schema field, or `anchor.<field>` from
    // TASK_TYPE_METRIC_ANCHOR_SUM_FIELDS (checked on save).
    field: z.string().trim().min(1).max(255),
    label,
  }),
]);

export type TaskTypeMetric = z.infer<typeof TaskTypeMetricSchema>;

export const TaskTypeMetricsSchema = z
  .array(TaskTypeMetricSchema)
  .max(MAX_TASK_TYPE_METRICS);

/**
 * The cards a task type shows. Cards are opt-in: a type that has not chosen
 * any shows none, so existing task pages look the same until someone adds
 * cards to their type.
 */
export function resolveTaskTypeMetrics(
  metrics: readonly TaskTypeMetric[] | null | undefined,
): readonly TaskTypeMetric[] {
  return metrics ?? [];
}
